/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import React, { useEffect } from 'react';
import { UniversalTrackerBlueprintMin, UniversalTrackerPlain } from '../../../types/universalTracker';
import { useSelector } from 'react-redux';
import { getInitiativeBlueprintFlexSearchMap } from '../../../selectors/blueprint';
import { RootState } from '../../../reducers';

const filterDefault = () => {
  return 1;
}

type ScoreFn = (question: UniversalTrackerPlain) => number;

type ReturnHook = [UniversalTrackerBlueprintMin[], (searchText: string) => void]

export const useSearchThrottle = (delay = 300, scoreFn?: ScoreFn): ReturnHook => {

  const { data: blueprintQuestions = [] } = useSelector((state: RootState) => state.initiativeBlueprintQuestions);
  const throttleTimeout = React.useRef<ReturnType<typeof setTimeout> | undefined>();
  const [value, setVal] = React.useState<UniversalTrackerBlueprintMin[]>([]);

  const index = useSelector(getInitiativeBlueprintFlexSearchMap);

  useEffect(() => {
    if (blueprintQuestions.length > 0) {
      setVal(blueprintQuestions);
    }
  }, [blueprintQuestions])

  const debouncedSetVal = (searchText: string) => {
    if (throttleTimeout.current) {
      clearTimeout(throttleTimeout.current);
    }

    const scoreFunction = scoreFn ?? filterDefault;

    throttleTimeout.current = setTimeout(() => {
      let initialQuestions = blueprintQuestions as UniversalTrackerPlain[];
      initialQuestions = initialQuestions.filter((question: UniversalTrackerPlain) => question.type !== 'calculation');
      if (searchText) {
        const results = index.search(searchText);
        const matchedIds = new Set(results.map((question) => question.result).flat())
        initialQuestions = initialQuestions.filter(question => {
          return matchedIds.has(question._id)
        })
      }
      const filterScores = initialQuestions
        .filter((question: UniversalTrackerPlain) => scoreFunction(question))
      setVal(filterScores)
    }, delay);
  };

  React.useEffect(() => {
    return () => {
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
      }
    };
  }, []);

  return [value, debouncedSetVal];
}
